/*
* Component edit view for authenticated users
*/

import { process } from '@progress/kendo-data-query';
import { MultiSelect } from '@progress/kendo-react-dropdowns';
import { GridColumn as Column, Grid, GridToolbar } from '@progress/kendo-react-grid';
import * as _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { Alert, Button, ButtonToolbar, Modal, Row, Well } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import * as MutateActions from '../actions/mutate';
import * as AppComp from '../common/app-component-base';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import * as LK from '../config/apilookupkeys';
import AppConfiguration from '../config/configuration';
import * as DetailFormCommon from '../inventory/detail-form/common';
import EditGridCommandCell from '../lookup-tables/edit-grid-command-cell';
import * as ReduxQueries from '../queries';
import * as ComponentCommon from './common';

import CellDropDown from '../lookup-tables/cell-dropdown';

class CellTextInput extends React.Component<any, any> {
    public constructor(props: any) {
        super(props);

        this.state = {
            value: _.get(props.dataItem, props.field, ''),
        };
    }

    public componentWillReceiveProps(nextProps: any) {
        const wasEditing = Formatters.getBool(this.props.dataItem, this.props.recordEditingField);
        const isEditing = Formatters.getBool(nextProps.dataItem, nextProps.recordEditingField);
        const currentId = Formatters.getStr(this.props.dataItem, '_id');
        const nextId = Formatters.getStr(nextProps.dataItem, '_id');

        if (!isEditing
            || currentId !== nextId
            || this.props.field !== nextProps.field
            || (!wasEditing && isEditing)) {
            this.setState({
                value: _.get(nextProps.dataItem, nextProps.field, ''),
            });
        }
    }

    public render() {
        const { dataItem, field, handleInputChange, handleInputCommit, recordEditingField, ...rest } = this.props;
        const isEditing = Formatters.getBool(dataItem, recordEditingField);

        if (!isEditing) {
            let displayValue = Formatters.getStr(dataItem, field);
            if (field === 'cost') {
                displayValue = Formatters.formatCurrencyIntl(dataItem[field], 2);
            }
            return (
                <td>
                    {displayValue}
                </td>
            );
        }

        return (
            <td>
                <input
                    className="k-textbox"
                    style={{ width: '100%' }}
                    value={this.state.value || ''}
                    onChange={(e: any) => {
                        const value = _.get(e, 'target.value', '');
                        this.setState({ value });

                        if (handleInputChange) {
                            handleInputChange({
                                dataItem,
                                field,
                                syntheticEvent: e,
                                value,
                            });
                        }
                    }}
                    onBlur={(e: any) => {
                        if (handleInputCommit) {
                            handleInputCommit({
                                dataItem,
                                field,
                                syntheticEvent: e,
                                value: this.state.value || '',
                            });
                        }
                    }}
                />
            </td>
        );
    }
}
 
class CellMultiSelect extends React.Component<any, any> {
    public render() {
        const { dataItem, field, recordEditingField, handleMultiSelectChange, dropdownData, ...rest } = this.props;
        const selectedIds = _.get(dataItem, field, []) || [];
        const isEditing = Formatters.getBool(dataItem, recordEditingField);
 
        if (!isEditing) {
            if (_.size(selectedIds) === 0) {
                return (
                    <td>General</td>
                );
            }
            const selectedNames = selectedIds.map((id: number) => {
                const region: any = _.find(dropdownData, { value: id });
                return region ? region.text : `Region ${id}`;
            });
            return (
                <td>{selectedNames.join(', ')}</td>
            );
        }
 
        const selectedValues = dropdownData.filter((c: any) => selectedIds.includes(c.value));
 
        return (
            <td className="lookup-tables-table-dropdown-cell-edit">
                <div className="lookup-tables-table-dropdown-cell-container">
                    <MultiSelect
                        onChange={(e: any) => {
                            const newIds = (_.get(e, 'target.value') || []).map((val: any) => val.value);
                            if (handleMultiSelectChange) {
                                handleMultiSelectChange({
                                    dataItem,
                                    field,
                                    value: newIds,
                                    syntheticEvent: e,
                                });
                            }
                        }}
                        value={selectedValues}
                        data={dropdownData}
                        textField="text"
                        dataItemKey="value"
                    />
                </div>
            </td>
        );
    }
}
 
export interface IComponentEditProps extends AppComp.IAppComponentProps {
    componentObjId?: any;
    componentDetail?: any;
    componentReplacementCosts?: any;
    groupComponents?: any;
    componentCategories?: any;
    navigateTo?: any;
    forceRequest?: any;
    groups?: any;
    lookups?: any;
    updateReplacementCost?: any;
    deleteReplacementCost?: any;
    updateComponent?: any;
    newReplacementCost?: any;
    newStandardEvaluation?: any;
    updateStandardEvaluation?: any;
    deleteStandardEvaluation?: any;
}

export interface IComponentEditState extends AppComp.IAppComponentState {
    filterAssociation?: any;
    filterClient?: any;
    formChanges?: any;
    skipAssociation?: any;
    skipClient?: any;
    editData?: any;
    additionalGridData?: any;
    editDataStandardEvaluation?: any;
    additionalGridDataStandardEvaluation?: any;
    showModalRemoveGridItem?: boolean;
    showModalRemoveStandardEvaluationGridItem?: boolean;
}

class EditComponent extends AppComp.AppComponent<IComponentEditProps, IComponentEditState> {
    public MomentDateFormat: string = AppConfiguration.getMomentShortDateFormat();
    public MomentDateTimeFormat: string = AppConfiguration.getMomentDateTimeFormat();
    public originalFormDataCache = {};
    public readonly virtualizedDropdownPageSize: number = 20;
    public readonly GridEditField: string = 'editField';
    public readonly GridNewRecordField: string = 'newRecordField';
    public pendingGridCellChanges: any = {};
    public pendingGridCellChangesStandardEvaluation: any = {};

    public kendoFormWhiteBoxSectionProps = {};

    public constructor(props: IComponentEditProps) {
        super(props, 'app.component-detail.');

        this.state = {
            filterAssociation: undefined,
            filterClient: undefined,
            formChanges: {},
            skipAssociation: 0,
            skipClient: 0,
            editData: {},
            additionalGridData: undefined,
            editDataStandardEvaluation: {},
            additionalGridDataStandardEvaluation: undefined,
            showModalRemoveGridItem: false,
            showModalRemoveStandardEvaluationGridItem: false,
        };
    }

    public BuildGridTextInputCell = (props) => (
        <CellTextInput
            handleInputChange={(a) => { this.handleGridCellPendingChange(a); }}
            handleInputCommit={(a) => { this.handleGridCellCommit(a); }}
            recordEditingField={this.GridEditField}
            {...props}
        />
    )

    public BuildGridTextInputCellStandardEvaluation = (props) => (
        <CellTextInput
            handleInputChange={(a) => { this.handleGridCellPendingChangeStandardEvaluation(a); }}
            handleInputCommit={(a) => { this.handleGridCellCommitStandardEvaluation(a); }}
            recordEditingField={this.GridEditField}
            {...props}
        />
    )

    public componentDidUpdate() {
        let resetFormData = this.getFormChangesResetRequired();

        if (resetFormData && _.size(this.state.formChanges)) {
            this.setState({ formChanges: {} });
        }
    }

    // Data transforms
    public formattedDate(date: any) {
        // Returns a formatted date, or empty

        return date ? moment(new Date(date)).format(this.MomentDateFormat) : '';
    }

    public extendData(component: any) {
        if (!component) { return {}; }

        let record = _.cloneDeep(component);

        return record;
    }

    // Handlers
    public handleSaveButton(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');

        const { componentDetail, componentObjId, componentReplacementCosts, groups, componentCategories, groupComponents, lookups, ...rest } = this.props;

        const componentExtended = this.extendData(componentDetail);

        const formData = this.getFormData(componentExtended, componentReplacementCosts, groups, componentCategories, groupComponents);
        const component = _.get(formData, 'component');
        const editData = this.state.editData;
        const additionalGridData = this.state.additionalGridData;
        const editDataStandardEvaluation = this.state.editDataStandardEvaluation;
        const changeData = _.merge(
            {},
            { obj: _.pick(component, ['_id', 'inactive', 'componentId', 'name', 'summary', 'componentCategoryId', 'usefulLife', 'locations']) },
            { componentObjId: componentObjId },
            { editDataStandardEvaluation: editDataStandardEvaluation },
            { editData: editData },
            { additionalGridData: additionalGridData },
        );

        this.props.updateComponent(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};
                this.setState({
                    editData: {},
                    formChanges: {},
                    additionalGridData: undefined,
                    editDataStandardEvaluation: {},
                    additionalGridDataStandardEvaluation: undefined,
                });
                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleUpdateReplacementCost(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');

        const { componentDetail, componentObjId, componentReplacementCosts, groups, componentCategories, groupComponents, lookups, ...rest } = this.props;

        const componentExtended = this.extendData(componentDetail);

        const formData = this.getFormData(componentExtended, componentReplacementCosts, groups, componentCategories, groupComponents);
        const component = _.get(formData, 'component');

        const editData = this.mergePendingGridCellChanges(_.cloneDeep(this.state.editData));
        const costVal = Formatters.getFloat(editData, 'cost');
        _.set(editData, 'cost', costVal);
        const additionalGridData = this.state.additionalGridData;
 
        const changeData = _.merge(
            {},
            { replacementCostObjId: Formatters.getStr(editData, '_id') },
            { obj: _.pick(editData, ['_id', 'cost', 'explanation', 'notes', 'groupId', 'replacementCostTypeId', 'regions']) },
            { componentObjId: componentObjId },
        );

        this.props.updateReplacementCost(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.pendingGridCellChanges = {};

                this.setState({
                    editData: {},
                    additionalGridData: undefined,
                });
                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleSaveButtonStandardEvaluation(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');

        const componentObjId = Formatters.getStr(this.props, 'componentObjId');

        const curEditDataStandardEvaluation = this.mergePendingGridCellChangesStandardEvaluation(_.cloneDeep(_.get(this.state, 'editDataStandardEvaluation')));

        const changeData = _.merge(
            {},
            { obj: _.pick(curEditDataStandardEvaluation, ['_id', 'groupId', 'conditionLevel', 'evaluation', 'standardEvaluationId', 'groupCode', 'groupCodeDescriptor', 'regions']) },
            { componentObjId: componentObjId },
            { groupComponentObjId: Formatters.getStr(curEditDataStandardEvaluation, 'groupComponentObjId') },
        );

        // console.log(`handleSaveButtonStandardEvaluation`, { changeData, curEditDataStandardEvaluation });

        this.props.updateStandardEvaluation(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.pendingGridCellChangesStandardEvaluation = {};
                this.setState({
                    editDataStandardEvaluation: {},
                    additionalGridDataStandardEvaluation: undefined,
                });

                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleDeleteButton(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');

        const { componentDetail, componentObjId, componentReplacementCosts, groups, componentCategories, groupComponents, lookups, ...rest } = this.props;

        const componentExtended = this.extendData(componentDetail);

        const formData = this.getFormData(componentExtended, componentReplacementCosts, groups, componentCategories, groupComponents);
        const component = _.get(formData, 'component');

        let compObj = _.pick(component, ['_id', 'inactive']);
        _.set(compObj, 'inactive', true);

        const changeData = _.merge(
            {},
            { obj: compObj },
            { componentObjId: componentObjId },
        );

        this.props.updateComponent(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};
                this.setState({
                    editData: {},
                    formChanges: {},
                    additionalGridData: undefined,
                    editDataStandardEvaluation: {},
                    additionalGridDataStandardEvaluation: undefined,
                });
                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleFormInputChange(e: any, x: any) {
        // console.log('handleForminputchange', {e: e, x: x });

        const getChangesResult = this.getFormChanges(e, x);
        // console.log('handleForminputchange getChangesResult', {getChangesResult: getChangesResult, e: e, x: x });
        if (getChangesResult.applyChanges) {
            this.setState({
                formChanges: getChangesResult.formChanges
            });
        }
    }

    public handleCellDropdownChange(e: any) {
        let editExisting = _.cloneDeep(_.get(this.state, 'editData', {}));
        let modifyNew = _.cloneDeep(_.get(this.state, 'additionalGridData', {}));

        let val = _.get(e, 'target.value', {});
        const groupId = parseInt(_.get(val, 'value', 0), 10);
        _.set(editExisting, 'groupId', groupId);
        _.set(modifyNew, 'groupId', groupId);
        this.setState({ editData: editExisting, additionalGridData: modifyNew });
    }

    public handleCellDropdownChangeStandardEvaluation(e: any) {
        let editExisting = _.cloneDeep(_.get(this.state, 'editDataStandardEvaluation', {}));
        let modifyNew = _.cloneDeep(_.get(this.state, 'additionalGridDataStandardEvaluation', {}));
 
        let val = _.get(e, 'target.value', {});
        const groupId = parseInt(_.get(val, 'value', 0), 10);
        _.set(editExisting, 'groupId', groupId);
        _.set(modifyNew, 'groupId', groupId);
        this.setState({ editDataStandardEvaluation: editExisting, additionalGridDataStandardEvaluation: modifyNew });
    }
 
    public handleCellMultiSelectChange(e: any) {
        let editExisting = _.cloneDeep(_.get(this.state, 'editData', {}));
        let modifyNew = _.cloneDeep(_.get(this.state, 'additionalGridData', {}));
 
        let val = _.get(e, 'value', []);
        _.set(editExisting, 'regions', val);
        _.set(modifyNew, 'regions', val);
        this.setState({ editData: editExisting, additionalGridData: modifyNew });
    }
 
    public handleCellMultiSelectChangeStandardEvaluation(e: any) {
        let editExisting = _.cloneDeep(_.get(this.state, 'editDataStandardEvaluation', {}));
        let modifyNew = _.cloneDeep(_.get(this.state, 'additionalGridDataStandardEvaluation', {}));
 
        let val = _.get(e, 'value', []);
        _.set(editExisting, 'regions', val);
        _.set(modifyNew, 'regions', val);
        this.setState({ editDataStandardEvaluation: editExisting, additionalGridDataStandardEvaluation: modifyNew });
    }

    public handleComponentLocationsInputChange(e: any, o: any) {
        const fieldName = _.get(e, 'target.name', '');
        const newValue = _.get(e, 'target.value', []);

        let prevChanges = this.state.formChanges;
        _.set(prevChanges, 'component.locations', newValue);

        this.setState({
            formChanges: prevChanges
        });
    }

    public handleEnterEdit(d: any) {
        this.pendingGridCellChanges = {};
 
        let editData = {
            _id: d._id,
            cost: d.cost,
            explanation: d.explanation,
            notes: d.notes,
            groupId: d.groupId,
            regions: d.regions || [],
            replacementCostTypeId: d.replacementCostType.replacementCostTypeId,
            replacementCostType: d.replacementCostType,
            editField: true,
            newRecordField: false,
        };

        this.setState({
            editData: _.cloneDeep(editData),
            additionalGridData: undefined,
        });
    }

    public handleEnterEditStandardEvaluation(d: any) {
        this.pendingGridCellChangesStandardEvaluation = {};
        this.setState({
            editDataStandardEvaluation: _.cloneDeep(d),
            additionalGridDataStandardEvaluation: undefined,
        });
    }

    public handleEnterDetails(d: any) {
        // this.props.navigateTo(NavigationLinks.lookupTableEditChildObj(tableName, childObjId));
    }

    public handleCancel(d: any) {
        this.pendingGridCellChanges = {};

        this.setState({
            editData: {},
            additionalGridData: undefined,
        });
    }

    public handleCancelStandardEvaluation(d: any) {
        this.pendingGridCellChangesStandardEvaluation = {};
        this.setState({
            editDataStandardEvaluation: {},
            additionalGridDataStandardEvaluation: undefined,
        });
    }

    public handleCreate(d: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');
        const componentObjId = Formatters.getStr(this.props, 'componentObjId');
        const replacementCostObjId = Formatters.getStr(d, '_id');

        const mergedData = this.mergePendingGridCellChanges(d);
        const costVal = Formatters.getFloat(mergedData, 'cost');
        _.set(mergedData, 'cost', costVal);
 
        const changeData = _.merge(
            {},
            { obj: _.pick(mergedData, ['_id', 'cost', 'explanation', 'notes', 'groupId', 'replacementCostTypeId', 'regions']) },
            { componentObjId: componentObjId },
            { replacementCostObjId: replacementCostObjId }
        );

        this.props.newReplacementCost(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.pendingGridCellChanges = {};

                this.setState({
                    editData: {},
                    additionalGridData: undefined,
                });
                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleCreateStandardEvaluation(d: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');

        const componentObjId = Formatters.getStr(this.props, 'componentObjId');
        const mergedData = this.mergePendingGridCellChangesStandardEvaluation(d);

        const changeData = _.merge(
            {},
            { obj: _.pick(mergedData, ['_id', 'groupId', 'conditionLevel', 'evaluation', 'groupCode', 'groupCodeDescriptor', 'regions']) },
            { componentObjId: componentObjId },
        );

        // console.log(`handleCreateStandardEvaluation`, { changeData, d });

        this.props.newStandardEvaluation(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.pendingGridCellChangesStandardEvaluation = {};
                this.setState({
                    editDataStandardEvaluation: {},
                    additionalGridDataStandardEvaluation: undefined,
                });

                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleAddNewButton(e: any, replacementCostTypeId: number) {
        this.pendingGridCellChanges = {};

        let newReplacementCostObj = {
            cost: 0,
            notes: '',
            groupId: 1,
            explanation: '',
            regions: [],
            _id: '_new_replacementCost',
            replacementCostTypeId: replacementCostTypeId,
            replacementCostType: {
                replacementCostTypeId: replacementCostTypeId,
                name: '',
                description: '',
            },
            [this.GridEditField]: true,
            [this.GridNewRecordField]: true,
        };

        this.setState({
            editData: newReplacementCostObj,
            additionalGridData: newReplacementCostObj
        });
    }

    public handleAddNewStandardEvaluationButton(e: any, componentId: number) {
        this.pendingGridCellChangesStandardEvaluation = {};
        let newStandardEvaluationObj = {
            groupId: 1,
            componentId: componentId,
            conditionLevel: 1,
            groupCode: '',
            groupCodeDescriptor: '',
            evaluation: '',
            regions: [],
            _id: '_new_standard_evaluation',
            [this.GridEditField]: true,
            [this.GridNewRecordField]: true,
        };

        this.setState({
            editDataStandardEvaluation: newStandardEvaluationObj,
            additionalGridDataStandardEvaluation: newStandardEvaluationObj
        });
    }

    public handleShowModalRemoveGridItem(e: any, selectedDataItem: any) {
        this.setState({ showModalRemoveGridItem: true, editData: _.cloneDeep(selectedDataItem) });
    }

    public handleCloseModalRemoveGridItem() {
        this.setState({ showModalRemoveGridItem: false, editData: {} });
    }

    public handleConfirmRemoveGridItem(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');
        const curEdit = _.get(this.state, 'editData', {});
        const componentObjId = Formatters.getStr(this.props, 'componentObjId');
        const replacementCostObjId = Formatters.getStr(curEdit, '_id');

        const changeData = _.merge(
            {},
            { obj: _.pick(curEdit, ['_id']) },
            { componentObjId: componentObjId },
            { replacementCostObjId: replacementCostObjId }
        );

        this.props.deleteReplacementCost(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.setState({
                    editData: {},
                    additionalGridData: undefined,
                    showModalRemoveGridItem: false,
                });
                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleShowModalRemoveStandardEvaluationGridItem(e: any, selectedDataItem: any) {
        this.setState({ showModalRemoveStandardEvaluationGridItem: true, editDataStandardEvaluation: _.cloneDeep(selectedDataItem) });
    }

    public handleCloseModalRemoveStandardEvaluationGridItem() {
        this.setState({ showModalRemoveStandardEvaluationGridItem: false, editDataStandardEvaluation: {} });
    }

    public handleConfirmRemoveStandardEvaluationGridItem(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');

        const componentObjId = Formatters.getStr(this.props, 'componentObjId');

        const curEditDataStandardEvaluation = _.get(this.state, 'editDataStandardEvaluation');

        const changeData = _.merge(
            {},
            { obj: _.pick(curEditDataStandardEvaluation, ['_id', 'groupId', 'conditionLevel', 'evaluation', 'standardEvaluationId', 'groupCode', 'groupCodeDescriptor']) },
            { componentObjId: componentObjId },
            { groupComponentObjId: Formatters.getStr(curEditDataStandardEvaluation, 'groupComponentObjId') },
        );

        this.props.deleteStandardEvaluation(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.pendingGridCellChangesStandardEvaluation = {};
                this.setState({
                    editDataStandardEvaluation: {},
                    additionalGridDataStandardEvaluation: undefined,
                    showModalRemoveStandardEvaluationGridItem: false,
                });
                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleStandardEvaluationGridCellPendingChange(e: any) {
        const dataItem = _.get(e, 'dataItem', {});
        const rowId = Formatters.getStr(dataItem, '_id');
        const field = Formatters.getStr(e, 'field');

        if (!rowId || !field) { return; }

        const pending = _.get(this.pendingGridCellChangesStandardEvaluation, rowId, {});
        _.set(pending, field, _.get(e, 'value'));
        _.set(this.pendingGridCellChangesStandardEvaluation, rowId, pending);
    }

    public handleStandardEvaluationGridCellCommit(e: any) {
        this.handleStandardEvaluationGridCellPendingChange(e);
    }

    public mergeStandardEvaluationPendingChanges(d: any) {
        const rowId = Formatters.getStr(d, '_id');
        const pending = _.get(this.pendingGridCellChangesStandardEvaluation, rowId, {});

        return (_.isEmpty(pending)) ? d : _.merge({}, d, pending);
    }

    public handleGridItemChange(e: any) {
        let prev = _.cloneDeep(_.get(this.state, 'editData', {}));

        let n = Formatters.getStr(e, 'field');
        if (!n) { return; }

        let v = _.get(e, 'value');
        if (_.isUndefined(v)) {
            v = _.get(e, 'syntheticEvent.target.value');
        }
        if (_.isUndefined(v)) {
            v = _.get(e, `dataItem.${n}`);
        }
        if (_.isUndefined(v)) { return; }

        _.set(prev, n, v);
        this.setState({ editData: prev });
    }

    public handleGridItemChangeStandardEvaluation(e: any) {
        let prev = _.cloneDeep(_.get(this.state, 'editDataStandardEvaluation', {}));

        let n = Formatters.getStr(e, 'field');
        if (!n) { return; }

        let v = _.get(e, 'value');
        if (_.isUndefined(v)) {
            v = _.get(e, 'syntheticEvent.target.value');
        }
        if (_.isUndefined(v)) {
            v = _.get(e, `dataItem.${n}`);
        }
        if (_.isUndefined(v)) { return; }

        _.set(prev, n, v);
        this.setState({ editDataStandardEvaluation: prev });
    }

    public handleGridCellPendingChange(e: any) {
        const dataItem = _.get(e, 'dataItem', {});
        const rowId = Formatters.getStr(dataItem, '_id');
        const field = Formatters.getStr(e, 'field');

        if (!rowId || !field) { return; }

        let pending = _.get(this.pendingGridCellChanges, rowId, {});
        _.set(pending, field, _.get(e, 'value'));
        _.set(this.pendingGridCellChanges, rowId, pending);
    }

    public handleGridCellCommit(e: any) {
        // Keep text edits pending so blur does not trigger a grid re-render.
        this.handleGridCellPendingChange(e);
    }

    public mergePendingGridCellChanges(d: any) {
        const rowId = Formatters.getStr(d, '_id');
        const pending = _.get(this.pendingGridCellChanges, rowId, {});

        return (_.isEmpty(pending)) ? d : _.merge({}, d, pending);
    }

    public handleGridCellPendingChangeStandardEvaluation(e: any) {
        const dataItem = _.get(e, 'dataItem', {});
        const rowId = Formatters.getStr(dataItem, '_id');
        const field = Formatters.getStr(e, 'field');

        if (!rowId || !field) { return; }

        let pending = _.get(this.pendingGridCellChangesStandardEvaluation, rowId, {});
        _.set(pending, field, _.get(e, 'value'));
        _.set(this.pendingGridCellChangesStandardEvaluation, rowId, pending);
    }

    public handleGridCellCommitStandardEvaluation(e: any) {
        // Keep text edits pending so blur does not trigger a grid re-render.
        this.handleGridCellPendingChangeStandardEvaluation(e);
    }

    public mergePendingGridCellChangesStandardEvaluation(d: any) {
        const rowId = Formatters.getStr(d, '_id');
        const pending = _.get(this.pendingGridCellChangesStandardEvaluation, rowId, {});

        return (_.isEmpty(pending)) ? d : _.merge({}, d, pending);
    }
    // Helpers
    public getFormChanges(e: any, x: any) {
        let result = {
            applyChanges: false,
            formChanges: {},
            formChangesResult: {},
        };

        let fieldName = '';
        let newValue;

        let formChangesCopy = _.cloneDeep(this.state.formChanges);

        if (_.get(e, 'target.props.name')) {
            // Kendo form element
            if (_.has(e, 'target.combobox')) {
                // combobox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'target.value');
            } else {
                // assume textbox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'value');
            }
        } else {
            const eventTargetType = _.get(e, 'target.type', '');

            switch (eventTargetType) {
                case 'textarea':
                case 'text':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                case 'kendo-dropdownlist':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value', '');
                    break;

                case 'virtualized-select':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(x, 'value');
                    break;

                case 'delete-button':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                default:
                    // console.log(`*** handleFormInputChange switch hit default for eventTargetType ${eventTargetType}`);
                    break;
            }
        }

        // console.log(`handleFormInputChange continuing 1 fieldName ${fieldName} newValue ${newValue}`, { e: e, x: x });

        if (!fieldName) { return result; }

        let applyChange = false;
        let origVal = _.get(this.originalFormDataCache, fieldName);
        let formChangesResult = {};

        // console.log(`handleFormInputChange field ${fieldName} origVal ${origVal} newValue ${newValue}`);

        if (_.isArray(origVal)) {
            if (_.isArray(newValue)) {
                if (!_.isEqual(origVal, newValue)) {
                    _.set(formChangesCopy, fieldName, _.cloneDeep(newValue));
                    _.set(formChangesResult, fieldName, _.cloneDeep(newValue));
                    applyChange = true;
                } else {
                    applyChange = false;
                }
            } else {
                if (_.includes(origVal, newValue)) {
                    // console.log(`original array already contained val ${newValue} origVal`, origVal);
                    applyChange = false;
                } else {
                    const formChangesArr = _.get(formChangesCopy, fieldName, []);
                    // console.log(`${fieldName} formChangesArr`, formChangesArr);
                    if (_.includes(formChangesArr, newValue)) {
                        // console.log(`formChangesArr field ${fieldName} array already contains val ${newValue} formChangesArr`, formChangesArr);
                        applyChange = false;
                    } else {
                        formChangesArr.push(newValue);
                        _.set(formChangesCopy, fieldName, _.cloneDeep(formChangesArr));
                        _.set(formChangesResult, fieldName, _.cloneDeep(formChangesArr));
                        applyChange = true;
                        // console.log(`formChangesArr field ${fieldName} array DID NOT have val ${newValue}, applying change`, formChangesArr);
                    }
                }
            }
        } else {
            if (String(newValue) !== String(origVal)) {
                // New value
                _.set(formChangesCopy, fieldName, newValue);
                _.set(formChangesResult, fieldName, newValue);
                applyChange = true;
            } else {
                // Same as orig val
                if (_.has(formChangesCopy, fieldName)) {
                    _.unset(formChangesCopy, fieldName);
                    _.set(formChangesResult, fieldName, newValue);
                    applyChange = true;
                }
            }
        }

        if (applyChange) {
            result.applyChanges = true;
            result.formChanges = _.cloneDeep(formChangesCopy);
            result.formChangesResult = _.cloneDeep(formChangesResult);
        }

        // console.log(`getFormChanges returns result`, result);

        return result;
    }

    public getGridCol(fieldName: string, colTitle: string, colOptions: any = {}) {
        const result = _.merge(
            {},
            {
                f: fieldName,
                t: colTitle,
                p: colOptions,
            });

        return result;
    }

    public getFormData(componentIn: any, componentReplacementCostsIn: any, groupsIn: any, componentCategoriesIn: any, groupComponentsIn: any) {
        const formChangesComponent = _.get(this.state, 'formChanges.component', {});
        const component = _.merge({}, componentIn, formChangesComponent);

        const formChangesComponentReplacementCosts = _.get(this.state, 'formChanges.componentReplacementCosts', []);
        const componentReplacementCosts = _.merge({}, componentIn, formChangesComponent);

        const componentCategoryOptions = (_.size(componentCategoriesIn)) ?
            _.orderBy(componentCategoriesIn.map((c) => _.merge({}, { value: Formatters.getInt(c, 'categoryId'), text: `${Formatters.getStr(c, 'categoryId')} - ${Formatters.getStr(c, 'name')}` })), 'value')
            : [];

        let resultComponent = ComponentCommon.GetFormDataComponent(component);
        let result = {
            componentObjId: _.get(component, '_id'),
            component: resultComponent,

            componentReplacementCosts: (_.size(componentReplacementCostsIn)) ? componentReplacementCostsIn : [],

            groups: (_.size(groupsIn)) ? groupsIn : [],
            groupComponents: (_.size(groupComponentsIn)) ? groupComponentsIn : [],

            componentCategoryOptions: (_.size(componentCategoryOptions)) ? componentCategoryOptions : [],
        };

        const res = _.merge(result, this.state.formChanges);

        return res;
    }

    public getFormChangesCount() {
        const result = _.size(this.state.formChanges);
        // console.log(`getFormChangesCount result ${result} len.formChanges ${_.size(this.state.formChanges)}`, { state_formChanges: this.state.formChanges });
        return result;
    }

    public getFormChangesResetRequired() {
        let resetFormData = false;
        // Props values
        const pa = Formatters.getStr(this.props, 'componentObjId');

        // Cached values
        const ca = Formatters.getStr(this.originalFormDataCache, 'componentObjId');

        // Determine if formdata state and cache needs reset due to changing associd or invid
        if (pa && ca) {
            resetFormData = (pa !== ca) ? true : false;
        }

        return resetFormData;
    }

    public getEditComponentPageTitle(appendStr: string = '', messagePath: string = 'page-title.edit-component', prependPath: boolean = true) {
        return this.appTextPageTitleAppendString(appendStr, messagePath, prependPath);
    }

    public isComponentIdValid(formData: any, componentId: number) {
        const { lookups, componentDetail, ...rest } = this.props;

        let result = true;

        const matchesComponentDetailId = (componentId === Formatters.getInt(componentDetail, 'componentId')) ? true : false;
        if (!matchesComponentDetailId) {
            const uniqComponentIds = _.uniq(_.map(_.get(lookups, 'components', []), 'componentId'));

            result = (_.includes(uniqComponentIds, componentId)) ? false : true;
            return result;
        }

        return result;
    }

    public canSaveStandardEvaluation(d: any) {
        const merged = this.mergePendingGridCellChangesStandardEvaluation(_.cloneDeep(d));
        let result = ((Formatters.getInt(merged, 'groupId') > 0)
            && (Formatters.getStr(merged, 'evaluation') !== '')
            && (Formatters.getInt(merged, 'conditionLevel') > 0)
            && (Formatters.getStr(merged, 'groupCode') !== '')) ? true : false;

        return result;
    }

    // Renderers
    public renderGridCol(c: any, i?: any) {
        let props: any = {
            key: `col-${i}`,
        };

        if (Formatters.getStr(c, 'f')) { _.merge(props, { field: Formatters.getStr(c, 'f') }); }
        if (Formatters.getStr(c, 't')) { _.merge(props, { title: Formatters.getStr(c, 't') }); }
        if (_.get(c, 'p', {})) { _.merge(props, _.get(c, 'p', {})); }

        let result = (<Column {...props} />);

        return result;
    }

    public renderModalRemoveGridItem() {
        return (
            <Modal enforceFocus={false} show={this.state.showModalRemoveGridItem} onHide={() => this.handleCloseModalRemoveGridItem()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.delete-replacement-cost.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.appText('modals.delete-replacement-cost.body')}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" onClick={(e) => { this.handleConfirmRemoveGridItem(e); }}>{this.appText('modals.delete-replacement-cost.delete')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalRemoveGridItem()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderModalRemoveStandardEvaluationGridItem() {
        return (
            <Modal enforceFocus={false} show={this.state.showModalRemoveStandardEvaluationGridItem} onHide={() => this.handleCloseModalRemoveStandardEvaluationGridItem()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.delete-standard-evaluation.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.appText('modals.delete-standard-evaluation.body')}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" onClick={(e) => { this.handleConfirmRemoveStandardEvaluationGridItem(e); }}>{this.appText('modals.delete-standard-evaluation.delete')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalRemoveStandardEvaluationGridItem()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderSectionComponentInformation(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        const inputPropsReadonly = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
            rowClassName: 'kendo-form-row-readonly'
        });

        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, {
            className: 'inventory-detail-input-full-width',
        });

        let inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
            colLabelXs: 5,
            colInputXs: 4,
        });

        let inputPropsTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            // className: 'inventory-detail-input-75',
            colLabelXs: 5,
            colInputXs: 7,
        });

        const inputPropsMultiSelect = _.merge({}, defaultInputProps, {
            className: 'inventory-detail-input-full-width',
            colLabelXs: 12,
            colInputXs: 12,
        });

        const compId = Formatters.getInt(formData, 'component.componentId');
        const componentIdValid = this.isComponentIdValid(formData, compId);

        const componentIdValidation = (!componentIdValid) ? (
            <KendoWrappers.KendoDisabledInputHidden
                name="_component.componentId_validation"
                label=" "
                value={(
                    <div className="association-inventory-alert-component-negative-rul">
                        <Alert bsStyle="warning">
                            <i className="fa fa-exclamation-triangle" /> {this.appText('component-information.component-id-already-in-use', true, `${compId}`)}
                        </Alert>
                    </div>)}
                {...inputPropsReadonly}
            />
        ) : (<div />);

        const sectionComponentInformation = (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('component-information')}>
                <KendoWrappers.KendoNumberInput
                    name="component.componentId"
                    disabled={false}
                    label={this.appText('component-information.component-id')}
                    value={formData.component.componentId}
                    min={1}
                    step={1}
                    format="#######"
                    {...inputProps}
                />

                {componentIdValidation}

                <KendoWrappers.KendoTextInputDebounced
                    name="component.name"
                    label={this.appText('component-information.component-name')}
                    value={Formatters.getStr(formData, 'component.name')}
                    {...inputPropsTextDebounced}
                />

                <KendoWrappers.KendoTextInputDebounced
                    name="component.summary"
                    label={this.appText('component-information.component-summary')}
                    value={Formatters.getStr(formData, 'component.summary')}
                    {...inputPropsTextDebounced}
                />

                <KendoWrappers.KendoCheckbox
                    name="component.inactive"
                    label={this.appText('component-information.component-inactive')}
                    value={Formatters.getBool(formData, 'component.inactive')}
                    {...inputProps}
                />

                <DetailFormCommon.InventoryDetailFormSectionSpacing />

                <KendoWrappers.KendoDropdownListInput
                    name="component.componentCategoryId"
                    clearable={false}
                    label={this.appText('component-information.component-category')}
                    value={formData.component.componentCategoryId}
                    data={formData.componentCategoryOptions}
                    textField="text"
                    valueField="value"
                    onChange={(o) => { this.handleFormInputChange({ target: { name: 'component.componentCategoryId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                    {...inputPropsVirtualizedSelect}
                />

                <DetailFormCommon.InventoryDetailFormSectionSpacing />

                <KendoWrappers.KendoNumberInput
                    name="component.usefulLife"
                    label={this.appText('component-information.component-useful-life')}
                    value={formData.component.usefulLife}
                    min={0}
                    step={1}
                    format="###"
                    {...inputProps}
                />
            </DetailFormCommon.InventoryDetailFormSection>
        );

        const sectionComponentLocations = (_.size(_.get(formData, 'component.locations', []))) ? (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('component-information-locations')}>
                <div className="component-details-locations-container">
                    <KendoWrappers.KendoMultiSelect
                        name="component.locations"
                        clearable={true}
                        // filterable={true}
                        // onFilterChange={(e) => { handleAssociationMultiSelectFilterChange(e, formData); }}
                        label={this.appText('component-information-locations')}
                        value={_.get(formData, 'component.locations', [])}
                        required={false}
                        // data={_.get(formData, 'component.locations', []).slice(this.state.skipAssociation, this.state.skipAssociation + virtualizedDropdownPageSize)}
                        data={_.get(formData, 'component.locations', [])}
                        // dataItemKey="value"
                        allowCustom={true}
                        // virtual={{
                        //     total: _.size(_.get(formData, 'associationOptions', [])),
                        //     pageSize: virtualizedDropdownPageSize,
                        //     skip: this.state.skipAssociation,
                        // }}
                        // onPageChange={(e) => { handleAssociationMultiSelectPageChange(e, formData); }}
                        // textField="text"
                        onChange={(o) => { this.handleComponentLocationsInputChange({ target: { name: 'component.locations', type: 'kendo-multiselect', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsMultiSelect}
                    />
                </div>
            </DetailFormCommon.InventoryDetailFormSection>
        ) : (<div />);

        const ExtendGridData = (rows: any, ceData: any, ceId: any, nameAdditionalGridData: string = 'additionalGridData', fd: any = {}) => {
            let ldr = _.cloneDeep(rows);

            let agd = _.get(this.state, nameAdditionalGridData);
            if (agd && !_.isEmpty(agd)) { ldr.unshift(agd); }

            const ldrFDGroups = _.get(fd, 'groups', []);
            let transformed = _.map(ldr, (r, i) => {
                let record = _.cloneDeep(r);
                const ldrId = Formatters.getStr(r, '_id');
                const ldrGroupId = (ceId === ldrId) ? Formatters.getInt(ceData, 'groupId') : Formatters.getInt(r, 'groupId');
                const ldrComponentGroup = _.find(ldrFDGroups, ['groupId', ldrGroupId]);

                let isEditing = (ceId === ldrId) ? true : false;

                if (isEditing) {
                    const editFieldOption = _.stubObject();
                    _.set(editFieldOption, this.GridEditField, isEditing);
                    record = _.merge({}, r, ceData, editFieldOption);
                    if (ceData && ceData.regions) {
                        record.regions = ceData.regions;
                    }
                }

                // 20200211 Grid shows component group name
                if (ldrComponentGroup) {
                    let gridFieldOption = _.stubObject();
                    _.set(gridFieldOption, 'groupName', Formatters.getStr(ldrComponentGroup, 'description') + ' (ID: ' + Formatters.getStr(ldrComponentGroup, 'groupId') + ')');
                    record = _.merge({}, record, gridFieldOption);
                }
                if (_.size(record) === 2) {
                    const editFieldOption = _.stubObject();
                    _.set(editFieldOption, this.GridEditField, isEditing);
                    record = _.merge({}, r, ceData, editFieldOption);
                }

                return record;
            });

            return transformed;
        };

        const curEditData = _.get(this.state, 'editData', {});
        const curEditId = Formatters.getStr(curEditData, '_id');

        /* BEST COSTS */
        const componentReplacementCostsSorted = _.orderBy(_.get(formData, 'componentReplacementCosts', []), ['replacementCostType.name', 'groupId', 'cost'], ['asc', 'asc', 'asc']);
        const compRepCostsBestExtended = ExtendGridData(_.filter(componentReplacementCostsSorted, (o) => Formatters.getInt(o, 'replacementCostType.replacementCostTypeId') === 1), curEditData, curEditId, 'additionalGridData', formData);
        const compRepCostsBestFiltered = _.filter(compRepCostsBestExtended, (o) => Formatters.getInt(o, 'replacementCostType.replacementCostTypeId') === 1);

        const editGridCommandCell = EditGridCommandCell(
            (dataItem: any) => this.handleEnterEdit(dataItem),
            (e: any, dataItem: any) => this.handleShowModalRemoveGridItem(e, dataItem),
            (dataItem: any) => this.handleUpdateReplacementCost(dataItem),
            // (dataItem: any) => this.handleSaveButton(dataItem),
            (dataItem: any) => this.handleCreate(dataItem),
            (dataItem: any) => this.handleCancel(dataItem),
            (dataItem: any) => undefined, // enterDetails
            (dataItem: any) => false, // showDetails
            (dataItem: any) => true, // this.handleShowDelete(dataItem),
            (dataItem: any) => true, // canSave(dataItem),
            this.GridEditField,
            this.GridNewRecordField,
            (m?: any, p?: any, t?: any) => this.appText(m, p, t)
        );

        let newGridBestRepCostsProps: any = {
            resizable: true,
            style: { width: '100%', height: '280px' },
            editField: this.GridEditField,
            itemChange: (e: any) => { this.handleGridItemChange(e); },
        };
        let gridBestRepCostsCols = _.stubArray();

        let newGridWorstRepCostsProps: any = {
            resizable: true,
            style: { width: '100%', height: '280px' },
            editField: this.GridEditField,
            itemChange: (e: any) => { this.handleGridItemChange(e); },
        };
        let gridWorstRepCostsCols = _.stubArray();

        let newGridStandardEvaluationProps: any = {
            resizable: true,
            style: { width: '100%', height: '280px' },
            editField: this.GridEditField,
        };
        let gridStandardEvaluationCols = _.stubArray();
        const standardEvaluationTextCell = { editable: false, cell: this.BuildGridTextInputCellStandardEvaluation };

        const buildFilteredOptionsStringValue = (lookupData: any, key: any, srcValueField: any = 'name', srcTextField: any = 'name', removeId: string = '_') => {
            const lData = _.get(lookupData, key, []);

            const l = _.reject(lData, (o) => Formatters.getStr(o, '_id') === removeId);

            if (_.size(l) <= 0) { return []; }
            return _.orderBy(l.map((c) => _.merge({}, { value: Formatters.getStr(c, srcValueField), text: Formatters.getStr(c, srcTextField) + ' (ID: ' + c.groupId + ')' })), 'text');
            // return _.orderBy(l.map((c) => _.merge({}, { value: Formatters.getStr(c, srcValueField), text: Formatters.getStr(c, srcTextField)})), 'text');
        };

        const dd = buildFilteredOptionsStringValue(formData, 'groups', 'groupId', 'description', '');

        const cellDropDownGroupName = (props) => (
            <CellDropDown
                recordEditingField={this.GridEditField}
                handleDropDownChange={(a) => { this.handleCellDropdownChange(a); }}
                dropdownData={dd}
                {...props}
            />
        );
 
        const regionsList = _.get(this.props.lookups, 'regions', []);
        const regionsDropdownData = _.orderBy(
            regionsList.map((r: any) => ({
                value: Formatters.getInt(r, 'regionId'),
                text: Formatters.getStr(r, 'name'),
            })),
            'text'
        );
 
        const cellMultiSelectRegions = (props: any) => (
            <CellMultiSelect
                recordEditingField={this.GridEditField}
                handleMultiSelectChange={(a: any) => { this.handleCellMultiSelectChange(a); }}
                dropdownData={regionsDropdownData}
                {...props}
            />
        );
 
        const cellMultiSelectRegionsStandardEvaluation = (props: any) => (
            <CellMultiSelect
                recordEditingField={this.GridEditField}
                handleMultiSelectChange={(a: any) => { this.handleCellMultiSelectChangeStandardEvaluation(a); }}
                dropdownData={regionsDropdownData}
                {...props}
            />
        );

        let renderGridToolbar = true;
        const addNewButtonDisabled = false;
        const BuildReplacementCostsGridToolbar = (replacementCostTypeId: number, buttonCaption: string) => (
            <GridToolbar>
                <Button
                    bsSize="xs"
                    bsStyle="success"
                    onClick={(e) => this.handleAddNewButton(e, replacementCostTypeId)}
                    disabled={addNewButtonDisabled}
                >
                    <i className="glyphicon glyphicon-plus" /> {buttonCaption}
                </Button>
            </GridToolbar>
        );

        const gridToolbarBestCosts = (renderGridToolbar) ? BuildReplacementCostsGridToolbar(1, this.appText('grid.add-new-best-cost')) : (<div />);

        // gridBestRepCostsCols.push(this.getGridCol('groupId', this.appTextGridColumnHeader('group-id'), { width: '80px', editable: true, editor: 'numeric' }));
        // gridBestRepCostsCols.push(this.getGridCol('groupName', this.appTextGridColumnHeader('group-id-name'), { width: '120px', editable: false, editor: 'text' }));
        // gridBestRepCostsCols.push(this.getGridCol('groupName', this.appTextGridColumnHeader('group-id-name'), { width: '160px', editable: true, cell: cellDropDownGroupName }));
        gridBestRepCostsCols.push(this.getGridCol('regions', 'Regions', { width: '180px', editable: true, cell: cellMultiSelectRegions }));
        gridBestRepCostsCols.push(this.getGridCol('cost', this.appTextGridColumnHeader('cost'), { width: '90px', editable: true, className: 'right-align', headerClassName: 'right-align', format: '{0:c2}', cell: this.BuildGridTextInputCell }));
        gridBestRepCostsCols.push(this.getGridCol('explanation', this.appTextGridColumnHeader('explanation'), { editable: true, cell: this.BuildGridTextInputCell }));
        gridBestRepCostsCols.push(this.getGridCol('notes', this.appTextGridColumnHeader('notes'), { editable: true, cell: this.BuildGridTextInputCell }));
        gridBestRepCostsCols.push(this.getGridCol('updateBestCostObj', ' ', { editable: false, width: '150px', filterable: false, cell: editGridCommandCell }));

        const gridNewBestCosts = (
            <Grid
                data={process(compRepCostsBestFiltered, {})}
                resizable={true}
                editField={this.GridEditField}

                itemChange={(e) => { this.handleGridItemChange(e); }}

                {...newGridBestRepCostsProps}
            >
                {gridToolbarBestCosts}
                {_.map(gridBestRepCostsCols, (c, i) => this.renderGridCol(c, i))}

            </Grid>
        );

        const sectionComponentReplacementCostsBestGrid = (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('component-replacement-costs.best-costs')}>
                {/*gridBestCosts*/ gridNewBestCosts}
            </DetailFormCommon.InventoryDetailFormSection>
        );

        /* WORST COSTS */
        const compRepCostsWorstExtended = ExtendGridData(_.filter(componentReplacementCostsSorted, (o) => Formatters.getInt(o, 'replacementCostType.replacementCostTypeId') === 2), curEditData, curEditId, 'additionalGridData', formData);
        const compRepCostsWorstFiltered = _.filter(compRepCostsWorstExtended, (o) => Formatters.getInt(o, 'replacementCostType.replacementCostTypeId') === 2);

        const gridToolbarWorstCosts = (renderGridToolbar) ? BuildReplacementCostsGridToolbar(2, this.appText('grid.add-new-worst-cost')) : (<div />);

        // gridWorstRepCostsCols.push(this.getGridCol('groupName', this.appTextGridColumnHeader('group-id-name'), { width: '160px', editable: true, cell: cellDropDownGroupName }));
        gridWorstRepCostsCols.push(this.getGridCol('regions', 'Regions', { width: '180px', editable: true, cell: cellMultiSelectRegions }));
        gridWorstRepCostsCols.push(this.getGridCol('cost', this.appTextGridColumnHeader('cost'), { width: '90px', editable: true, className: 'right-align', headerClassName: 'right-align', format: '{0:c2}', cell: this.BuildGridTextInputCell }));
        gridWorstRepCostsCols.push(this.getGridCol('explanation', this.appTextGridColumnHeader('explanation'), { editable: true, cell: this.BuildGridTextInputCell }));
        gridWorstRepCostsCols.push(this.getGridCol('notes', this.appTextGridColumnHeader('notes'), { editable: true, cell: this.BuildGridTextInputCell }));
        gridWorstRepCostsCols.push(this.getGridCol('updateWorstCostObj', ' ', { editable: false, width: '150px', filterable: false, cell: editGridCommandCell }));

        const gridNewWorstCosts = (
            <Grid
                data={process(compRepCostsWorstFiltered, {})}
                resizable={true}
                editField={this.GridEditField}

                itemChange={(e) => { this.handleGridItemChange(e); }}

                {...newGridWorstRepCostsProps}
            >
                {gridToolbarWorstCosts}

                {_.map(gridWorstRepCostsCols, (c, i) => this.renderGridCol(c, i))}
            </Grid>
        );

        const sectionComponentReplacementCostsWorstGrid = (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('component-replacement-costs.worst-costs')}>
                {/*gridWorstCosts*/ gridNewWorstCosts}
            </DetailFormCommon.InventoryDetailFormSection>
        );

        /* STANDARD EVALUATIONS */
        const curEditDataStandardEvaluation = _.get(this.state, 'editDataStandardEvaluation', {});
        const curEditIdStandardEvaluation = Formatters.getStr(curEditDataStandardEvaluation, '_id');
        const editStandardEvaluationGridCommandCell = EditGridCommandCell(
            (dataItem: any) => this.handleEnterEditStandardEvaluation(dataItem),
            (e: any, dataItem: any) => this.handleShowModalRemoveStandardEvaluationGridItem(e, dataItem),
            (dataItem: any) => this.handleSaveButtonStandardEvaluation(dataItem),
            (dataItem: any) => this.handleCreateStandardEvaluation(dataItem),
            (dataItem: any) => this.handleCancelStandardEvaluation(dataItem),
            (dataItem: any) => undefined,
            (dataItem: any) => false,
            (dataItem: any) => true, // this.handleShowDelete(dataItem),
            (dataItem: any) => this.canSaveStandardEvaluation(dataItem),
            this.GridEditField,
            this.GridNewRecordField,
            (m?: any, p?: any, t?: any) => this.appText(m, p, t),
            true,
        );

        const addNewEvaluationButtonDisabled = false;
        const BuildStandardEvaluationsGridToolbar = (buttonCaption: string, componentId: number) => (
            <GridToolbar>
                <Button
                    bsSize="xs"
                    bsStyle="success"
                    onClick={(e) => this.handleAddNewStandardEvaluationButton(e, componentId)}
                    disabled={addNewEvaluationButtonDisabled}
                >
                    <i className="glyphicon glyphicon-plus" /> {buttonCaption}
                </Button>
            </GridToolbar>
        );
        const groupComponentsSorted = _.orderBy(_.get(formData, 'groupComponents', []), ['componentId'], ['asc']);
        const groupComponentsData = _.compact(_.flattenDeep(_.compact(_.map(groupComponentsSorted, (o: any) => {
            let res = _.pick(o, ['componentId', 'groupId', 'groupObjectId']);
            const sce = _.get(o, 'standardComponentEvaluations', []);
            if (_.size(sce)) {
                const newRows = _.map(sce, (s: any) => {
                    let x = _.pick(s, ['conditionLevel', 'evaluation', 'standardEvaluationId']);

                    let groupCode = Formatters.getStr(o, 'groupCode');
                    let groupCodeDescriptor = Formatters.getStr(o, 'groupCodeDescriptor');
                    const nestedGroupCode = Formatters.getStr(s, 'groupCode');
                    const nestedGroupCodeDescriptor = Formatters.getStr(s, 'groupCodeDescriptor');
                    if (nestedGroupCode !== '') {
                        groupCode = nestedGroupCode;
                    }
                    if (nestedGroupCodeDescriptor !== '') {
                        groupCodeDescriptor = nestedGroupCodeDescriptor;
                    }

                    let y = {
                        '_id': `${o._id}-${s.standardEvaluationId}`,
                        'groupComponentObjId': o._id,
                        groupCode,
                        groupCodeDescriptor,
                    };

                    const ret = _.merge({}, y, res, x);
                    return ret;
                });

                return newRows;
            }
            return [];
        }))));

        const groupComponentsExtended = ExtendGridData(groupComponentsData, curEditDataStandardEvaluation, curEditIdStandardEvaluation, 'additionalGridDataStandardEvaluation', formData);
        const gridToolbarStandardEvaluations = BuildStandardEvaluationsGridToolbar(this.appText('grid.add-new-standard-evaluation'), compId);

        gridStandardEvaluationCols.push(this.getGridCol('conditionLevel', this.appTextGridColumnHeader('condition-level'), { width: '100px', ...standardEvaluationTextCell }));
        gridStandardEvaluationCols.push(this.getGridCol('groupCode', this.appTextGridColumnHeader('group-code'), { width: '120px', ...standardEvaluationTextCell }));
        gridStandardEvaluationCols.push(this.getGridCol('groupCodeDescriptor', this.appTextGridColumnHeader('group-code-descriptor'), { width: '200px', ...standardEvaluationTextCell }));
        gridStandardEvaluationCols.push(this.getGridCol('regions', 'Regions', { width: '180px', editable: true, cell: cellMultiSelectRegionsStandardEvaluation }));
        gridStandardEvaluationCols.push(this.getGridCol('evaluation', this.appTextGridColumnHeader('evaluation'), standardEvaluationTextCell));
        gridStandardEvaluationCols.push(this.getGridCol('', '', { width: '151px', cell: editStandardEvaluationGridCommandCell }));

        const gridStandardEvaluationsNew = (
            <Grid
                data={process(groupComponentsExtended, {})}
                editField={this.GridEditField}
                {...newGridStandardEvaluationProps}
            >
                {gridToolbarStandardEvaluations}
                {_.map(gridStandardEvaluationCols, (c, i) => this.renderGridCol(c, i))}
            </Grid>
        );

        const sectionComponentStandardEvaluationsGrid = (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('component-standard-evaluations')}>
                {/*gridStandardEvaluations*/ gridStandardEvaluationsNew}
            </DetailFormCommon.InventoryDetailFormSection>
        );

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('component-information')}>
                <Row>
                    <Col xs={5}>
                        {sectionComponentInformation}
                    </Col>

                    <Col xs={7}>
                        {sectionComponentLocations}
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        {sectionComponentReplacementCostsBestGrid}

                        {sectionComponentReplacementCostsWorstGrid}

                        {sectionComponentStandardEvaluationsGrid}
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderButtonToolbar = () => {
        let buttonProps = {
            bsSize: 'sm',
        };

        let saveChangesButtonProps = {
            bsStyle: 'default',
            bsSize: 'sm',
            disabled: true,
            title: this.appTextCommon('buttons.no-changes'),
        };

        let deleteButtonProps = {
            bsStyle: 'danger',
            bsSize: 'sm',
            disabled: false,
            title: this.appTextCommon('buttons.delete'),
        };

        if (this.getFormChangesCount()) {
            saveChangesButtonProps = {
                bsStyle: 'success',
                bsSize: 'sm',
                disabled: false,
                title: `${this.appTextCommon('buttons.save')} ${Formatters.plural(this.appTextCommon('buttons.change'), this.getFormChangesCount(), false)}`,
            };
        }

        return (
            <div>
                <Well>
                    <Row>
                        <Col xs={9}>
                            <ButtonToolbar>
                                <Button {...saveChangesButtonProps} onClick={e => { this.handleSaveButton(e); }}><i className="glyphicon glyphicon-floppy-disk" /> {saveChangesButtonProps.title}</Button>

                                <Button {...deleteButtonProps} onClick={e => { this.handleDeleteButton(e); }}><i className="glyphicon glyphicon-trash" /> {deleteButtonProps.title}</Button>
                            </ButtonToolbar>
                        </Col>
                        <Col xs={3} />
                    </Row>
                </Well>
            </div>
        );
    }

    public render() {
        const { componentDetail, componentObjId, componentReplacementCosts, groups, componentCategories, groupComponents, lookups, ...rest } = this.props;

        const component = this.extendData(componentDetail);
        const componentDocObjId = Formatters.getStr(component, '_id');

        let pageTitle = this.getEditComponentPageTitle('');

        let page = (<div />);

        if (componentObjId && componentDocObjId && componentObjId === componentDocObjId) {
            const formData = this.getFormData(component, componentReplacementCosts, groups, componentCategories, groupComponents);

            pageTitle = this.getEditComponentPageTitle(`${Formatters.getStr(formData, 'component.componentId')} - ${Formatters.getStr(formData, 'component.name')}`);

            // console.log('pageData', {
            //     componentObjId,
            //     component,
            //     componentDetail,
            //     formData,
            //     numChanges: this.getFormChangesCount(),
            //     formChanges: this.state.formChanges,
            // });

            if (this.getFormChangesResetRequired() || !_.size(this.originalFormDataCache)) {
                this.originalFormDataCache = _.cloneDeep(formData);
            }

            page = (
                <div>
                    {this.renderModalRemoveGridItem()}
                    {this.renderModalRemoveStandardEvaluationGridItem()}
                    {this.renderButtonToolbar()}
                    <Row>
                        <Col xs={12} xl={9}>
                            {/* Component details section */}
                            <Row>
                                <Col xs={12}>
                                    <div>
                                        {this.renderSectionComponentInformation(formData)}
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>
            );
        }

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrlLookups = () => ApiEndpoints.getLookupsByKeyUrl([
    LK.LookupKeys.Components,
    LK.LookupKeys.Regions,
]);

const getApiUrl = props => ApiEndpoints.getEditComponentDetailUrl(Formatters.getStr(props, 'match.params.componentObjId'));

const mapPropsToConfigs = (props) => {
    return [
        ReduxQueries.Queries.getEditComponentDetailRequest(
            getApiUrl(props),
            ReduxQueries.PropsAccessToken(props),
        ),

        ReduxQueries.Queries.getLookupsByKeyRequest(
            getApiUrlLookups(),
            ReduxQueries.PropsAccessToken(props)
        ),
    ];
};

const mapStateToProps = (state, props) => {
    const componentObjId = Formatters.getStr(props, 'match.params.componentObjId');

    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(props), state, props, {
        componentObjId: componentObjId,
        componentDetail: state.entities.editComponentDetailComponent,
        componentReplacementCosts: state.entities.editComponentDetailComponentReplacementCosts,
        componentCategories: state.entities.editComponentDetailComponentCategories,
        groups: state.entities.editComponentDetailGroups,
        groupComponents: state.entities.editComponentDetailGroupComponents,
    });
};

const mapDispatchToProps = {
    updateReplacementCost: MutateActions.updateReplacementCostMutateAsync,
    deleteReplacementCost: MutateActions.deleteReplacementCostMutateAsync,
    updateComponent: MutateActions.updateComponentMutateAsync,
    newReplacementCost: MutateActions.newReplacementCostMutateAsync,
    newStandardEvaluation: MutateActions.newStandardEvaluationMutateAsync,
    updateStandardEvaluation: MutateActions.updateStandardEvaluationMutateAsync,
    deleteStandardEvaluation: MutateActions.deleteStandardEvaluationMutateAsync,
    navigateTo: (path) => push(path),
};

const EditComponentContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(EditComponent);

export default withRouter(EditComponentContainer);



// WEBPACK FOOTER //
// ./src/component/edit.tsx